import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil, take, withLatestFrom, map, switchMap } from 'rxjs/operators';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromComm from 'src/app/modules/communications/store/communications';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { ConfiguredChannel, ChannelUIds } from 'src/app/modules/communications/store/communications/communications.model';
import { Location } from '@angular/common';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'app-channel-list',
  templateUrl: './channel-list.component.html',
  styleUrls: ['./channel-list.component.scss'],
  standalone: false
})
export class ChannelListComponent implements OnInit, OnDestroy {
  configuredChannels: ConfiguredChannel[] = [];
  isLoading = false;
  organizationChannelSearchText = '';
  destroy$ = new Subject<void>();
  queryParams$: Observable<QueryParams>;
  allOrganizationChannels: fromComm.OrganizationChannel[] = [];
  organizationChannels: fromComm.OrganizationChannel[] = [];
  channelToDelete: ConfiguredChannel = null;
  organizationUId = null;
  routeParamChannelUId: string = null;

  protected readonly userContext = inject(UserContextService);
  private notify = inject(SystemNotificationsService);

  constructor(
    private store: Store<fromComm.State>,
    private translate: TranslateService,
    private location: Location
  ) {}

  ngOnInit(): void {
    if (!this.userContext.canReadChannels()) {
      this.notify.error(this.translate.instant('COMMON.MISSING-PERMISSIONS'));
      return;
    }

    this.store.dispatch(fromComm.clearSelectedConfiguredChannel());
    this.store.dispatch(fromComm.getOrganizationChannelsFromApi());
    this.store.dispatch(fromComm.getConfiguredChannelsFromApi({ queryParams: null }));

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(take(1))
      .subscribe(userContext => {
        this.organizationUId = userContext.organizationUId;
      });

    this.store
      .select(fromComm.selectSelectedConfiguredChannelToDelete)
      .pipe(takeUntil(this.destroy$))
      .subscribe(channel => {
        this.channelToDelete = channel;
      });

    this.store
      .select(fromComm.selectOrganizationChannels)
      .pipe(takeUntil(this.destroy$))
      .subscribe(channels => {
        this.allOrganizationChannels = channels
          .map(c => {
            return {
              ...c,
              channelTypeDisplay: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${c.channelType}`),
              name: this.translate.instant(c.name)
            };
          })
          .sort((c1, c2) => {
            if (c1.channelTypeDisplay !== c2.channelTypeDisplay) {
              return c1.channelTypeDisplay > c2.channelTypeDisplay ? 1 : -1;
            }
            return c1.name > c2.name ? 1 : -1;
          });
        this.filterOrganizationChannels();
      });

    this.store
      .select(fromComm.selectConfiguredChannels)
      .pipe(withLatestFrom(this.store.select(fromRouting.selectRouteParams)), takeUntil(this.destroy$))
      .subscribe(([channels, params]) => {
        this.configuredChannels = channels.map(c => {
          return {
            ...c,
            channelTypeDisplay: this.translate.instant(`MANAGEMENT.COMMUNICATION-CENTER.CHANNEL-TYPE-${c.channelType}`),
            channelName: this.translate.instant(c.channelName)
          };
        });

        if (!this.routeParamChannelUId) {
          this.routeParamChannelUId = params?.id;
          let channel = this.configuredChannels.find(c => c.uId === this.routeParamChannelUId);

          if (channel) {
            this.selectConfiguredChannel(channel);
          }
        }
      });

    this.store.select(fromRouting.selectQueryParams).subscribe(queryParams => {
      if (!!queryParams && !!queryParams.error) {
        this.store.dispatch(fromComm.displayChannelError({ err: queryParams.error }));
      }
    });

    this.queryParams$ = this.store.select(fromComm.selectConfiguredChannelQueryParams);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  selectChannel(channel: fromComm.OrganizationChannel): void {
    switch (channel.channelUId) {
      case ChannelUIds.TopoEmailChannelUId:
        this.store.dispatch(fromComm.newTopoEmailChannel({ channel }));
        break;

      case ChannelUIds.SendGridEmailChannelUId:
        this.store.dispatch(fromComm.newSendGridEmailChannel({ channel }));
        break;

      case ChannelUIds.SlackChannelUId:
        this.store.dispatch(fromComm.newSlackChannel({ channel }));
        break;

      case ChannelUIds.EverbridgeChannelUId:
        this.store.dispatch(fromComm.newEverbridgeChannel({ channel }));
        break;

      case ChannelUIds.MicrosoftTeamsChannelUId:
        this.store.dispatch(fromComm.newTeamsChannel({ channel }));
        break;

      case ChannelUIds.TwilioChannelUId:
        this.store.dispatch(fromComm.newTwilioChannel({ channel }));
        break;

      case ChannelUIds.TwilioTtsChannelUId:
        this.store.dispatch(fromComm.newTwilioTtsChannel({ channel }));
        break;

      case ChannelUIds.WhatsAppChannelUId:
        this.store.dispatch(fromComm.newWhatsAppChannel({ channel }));
        break;

      case ChannelUIds.HorizonSmsChannelUId:
        this.store.dispatch(fromComm.newHorizonSmsChannel({ channel }));
        break;

      case ChannelUIds.HorizonMobilePushChannelUId:
        this.store.dispatch(fromComm.newHorizonMobilePushChannel({ channel }));
        break;

      case ChannelUIds.GwtsMobileChannelUId:
        this.store.dispatch(fromComm.newGwtsMobileChannel({ channel }));
        break;
    }
  }

  organizationChannelSearch(): void {
    this.filterOrganizationChannels();
  }

  filterOrganizationChannels() {
    if (!this.organizationChannelSearchText) {
      this.organizationChannels = this.allOrganizationChannels.slice();
      return;
    }

    this.organizationChannels = this.allOrganizationChannels.filter(
      c => c.name?.toLowerCase().indexOf(this.organizationChannelSearchText.toLowerCase()) > -1
    );
  }

  canSelectChannel(channel: fromComm.OrganizationChannel): boolean {
    if (channel.channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
      const teamChannels = this.configuredChannels?.filter(c => c.channelUId === ChannelUIds.MicrosoftTeamsChannelUId);
      const regularChannel = teamChannels?.some(c => c.settings?.sendDirectMessages !== true);
      const dmChannel = teamChannels?.some(c => c.settings?.sendDirectMessages === true);
      return !!!regularChannel || !!!dmChannel;
    } else {
      return true;
    }
  }

  editClick(channel: ConfiguredChannel) {
    this.selectConfiguredChannel(channel);
  }

  selectConfiguredChannel(channel: ConfiguredChannel) {
    // Change URL without actually initiating a router navigation so that we preserve the list state

    // ^^^ then why do we even need to get the router involved in the first place?!?! this is a stupid design...
    // this is just going out of the way and adding more steps and complexity just to set a selected UId that only gets used in this component...
    this.location.go(`/communications/channels/${channel.uId}`);

    switch (channel.channelUId) {
      case ChannelUIds.TopoEmailChannelUId:
        this.store.dispatch(fromComm.editTopoEmailChannel({ channel }));
        break;

      case ChannelUIds.SendGridEmailChannelUId:
        this.store.dispatch(fromComm.editSendGridEmailChannel({ channel }));
        break;

      case ChannelUIds.SlackChannelUId:
        this.store.dispatch(fromComm.editSlackChannel({ channel }));
        break;

      case ChannelUIds.EverbridgeChannelUId:
        this.store.dispatch(fromComm.editEverbridgeChannel({ channel }));
        break;

      case ChannelUIds.MicrosoftTeamsChannelUId:
        this.store.dispatch(fromComm.editMicrosoftTeamsChannel({ channel }));
        break;

      case ChannelUIds.TwilioChannelUId:
        this.store.dispatch(fromComm.editTwilioChannel({ channel }));
        break;

      case ChannelUIds.TwilioTtsChannelUId:
        this.store.dispatch(fromComm.editTwilioTtsChannel({ channel }));
        break;

      case ChannelUIds.WhatsAppChannelUId:
        this.store.dispatch(fromComm.editWhatsAppChannel({ channel }));
        break;

      case ChannelUIds.HorizonSmsChannelUId:
        this.store.dispatch(fromComm.editHorizonSmsChannel({ channel }));
        break;

      case ChannelUIds.HorizonMobilePushChannelUId:
        this.store.dispatch(fromComm.editHorizonMobilePushChannel({ channel }));
        break;

      case ChannelUIds.GwtsMobileChannelUId:
        this.store.dispatch(fromComm.editGwtsMobileChannel({ channel }));
        break;
    }
  }

  deleteClick(channel: ConfiguredChannel) {
    this.store.dispatch(fromComm.setChannelToDelete({ channel }));
  }

  confirmDeleteChannel(response: boolean) {
    if (response) {
      this.store.dispatch(fromComm.deleteConfiguredChannelApi({ channel: this.channelToDelete }));
    } else {
      this.store.dispatch(fromComm.clearChannelToDelete());
    }
  }

  isActiveClick(channel: ConfiguredChannel) {
    const channelToUpdate = {
      ...channel,
      isActive: !channel.isActive
    };

    this.store.dispatch(fromComm.saveConfiguredChannelToApi({ channel: channelToUpdate }));
  }

  onPaginate(skip: number): void {
    let params: QueryParams;
    this.store
      .select(fromComm.selectConfiguredChannelQueryParams)
      .pipe(take(1))
      .subscribe(value => (params = value));

    const newParams = {
      ...params,
      skip
    };
    this.store.dispatch(fromComm.getConfiguredChannelsFromApi({ queryParams: newParams }));
  }

  onSearch(searchTerm: string): void {
    let params: QueryParams;
    this.store
      .select(fromComm.selectConfiguredChannelQueryParams)
      .pipe(take(1))
      .subscribe(value => (params = value));

    const newParams = {
      ...params,
      skip: 0,
      where: !!searchTerm ? `Name.Contains("${searchTerm}")` : null
    };

    this.store.dispatch(fromComm.getConfiguredChannelsFromApi({ queryParams: newParams }));
  }

  onSort(sortExpression: { sortField: string; sortDirection: string }): void {
    let params: QueryParams;
    this.store
      .select(fromComm.selectConfiguredChannelQueryParams)
      .pipe(take(1))
      .subscribe(value => (params = value));

    const newParams = {
      ...params,
      skip: 0,
      sortField: sortExpression.sortField,
      sortDirection: sortExpression.sortDirection
    };

    this.store.dispatch(fromComm.getConfiguredChannelsFromApi({ queryParams: newParams }));
  }
}
